import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';

// Quotes a term for a PostgREST or() filter, so commas and brackets in it stay literal.
const like = (term: string) => `"%${term.replace(/["\\]/g, '\\$&')}%"`;

// Finds Users by email or name and Workspaces by name or brand domain. Uses the Staff's
// own session: the Staff read policies are what make other Users' rows visible.
export function StaffSearch() {
  const [params, setParams] = useSearchParams();
  const term = params.get('q') ?? '';
  const [input, setInput] = useState(term);

  const { data, isFetching, error } = useQuery({
    queryKey: ['staff-search', term],
    enabled: term.length > 0,
    queryFn: async () => {
      const q = like(term);
      // "Jane Doe" matches first and last name together.
      const [first, ...rest] = term.split(/\s+/);
      const fullName = rest.length ? `,and(first_name.ilike.${like(first)},last_name.ilike.${like(rest.join(' '))})` : '';
      const [users, workspaces] = await Promise.all([
        supabase
          .from('profiles')
          .select('id, email, first_name, last_name')
          .or(`email.ilike.${q},first_name.ilike.${q},last_name.ilike.${q}${fullName}`)
          .limit(25),
        supabase.from('workspaces').select('id, name, brand_url').or(`name.ilike.${q},brand_url.ilike.${q}`).limit(25),
      ]);
      if (users.error) throw users.error;
      if (workspaces.error) throw workspaces.error;
      return { users: users.data, workspaces: workspaces.data };
    },
  });

  return (
    <div className="staff-search">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setParams(input.trim() ? { q: input.trim() } : {});
        }}
      >
        <label htmlFor="staff-search" className="form_label">Email, name or brand domain</label>
        <input
          id="staff-search"
          type="search"
          className="form_input is-light w-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          autoFocus
        />
      </form>

      {isFetching && <div className="loading-spinner" />}
      {error && <p className="text-size-small staff-error">{error.message}</p>}

      {data && (
        <div className="staff-results">
          <section>
            <h2 className="text-size-medium text-weight-semibold">Users</h2>
            {data.users.length === 0 && <p className="text-size-small text-colour-alt-grey">No Users match.</p>}
            {data.users.map((u) => (
              <Link key={u.id} to={`/staff/users/${u.id}`} className="staff-result">
                <span>{[u.first_name, u.last_name].filter(Boolean).join(' ') || '(no name)'}</span>
                <span className="text-colour-alt-grey">{u.email}</span>
              </Link>
            ))}
          </section>
          <section>
            <h2 className="text-size-medium text-weight-semibold">Workspaces</h2>
            {data.workspaces.length === 0 && <p className="text-size-small text-colour-alt-grey">No Workspaces match.</p>}
            {data.workspaces.map((w) => (
              <Link key={w.id} to={`/staff/workspaces/${w.id}`} className="staff-result">
                <span>{w.name}</span>
                <span className="text-colour-alt-grey">{w.brand_url}</span>
              </Link>
            ))}
          </section>
        </div>
      )}
    </div>
  );
}
